import { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { ThumbsUp, ThumbsDown, Copy, Check } from 'lucide-react';
import { useTutorStore } from '@shared/state';
import { trackEvent, getBaseProperties } from '@shared/services/analytics';
import { ANALYTICS_EVENTS } from '@shared/constants';
import type { TutorMessage } from '@shared/types/tutor';
import { TutorCheckpoint } from './TutorCheckpoint';
import { DiagramRenderer } from '../learning/DiagramRenderer';
import { TutorVideoResults } from './TutorVideoResults';

// Detect CogniPrint profile references for gold chip treatment
function highlightProfileReferences(text: string): string {
  const patterns = [
    /since you prefer/gi,
    /based on your profile/gi,
    /for a visual learner like you/gi,
    /your CogniPrint shows/gi,
    /given your preference for/gi,
    /because you tend to/gi,
    /as someone who learns by/gi,
  ];
  let result = text;
  for (const pattern of patterns) {
    result = result.replace(pattern, (match) => `**${match}**`);
  }
  return result;
}

function MessageBubble({ message }: { message: TutorMessage }) {
  const submitFeedback = useTutorStore((s) => s.submitFeedback);
  const isUser = message.role === 'user';
  const [copied, setCopied] = useState(false);

  // Behavioural signal: explicit thumbs on a tutor answer (weight ~0.8 explicit).
  function handleFeedback(feedback: 'up' | 'down') {
    submitFeedback(message.messageId, feedback);
    trackEvent({
      name: ANALYTICS_EVENTS.TUTOR_FEEDBACK_GIVEN,
      properties: {
        ...getBaseProperties(),
        queryId: message.messageId,
        feedback,
        mode: String(message.mode ?? 'freeform'),
        nodeId: message.contextNodeId ?? null,
      },
    }).catch(() => {});
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard API may be unavailable (http, sandboxed iframe). Silently no-op.
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      <div className={`max-w-[90%] flex ${isUser ? 'ml-4 flex-row-reverse' : 'mr-4 flex-row'} gap-2`}>
        {/* Professor Cogni avatar on assistant messages */}
        {!isUser && (
          <img src="/CogHead.png" alt="Professor Cogni" className="w-6 h-6 object-contain shrink-0 mt-1" />
        )}
        <div
          className={`px-3 py-2.5 rounded-lg text-sm font-body leading-relaxed
            ${isUser
              ? 'bg-indigo-500/15 text-content'
              : 'bg-dark-subtle text-content-secondary'
            }
          `}
        >
          {isUser ? (
            <p>{message.content}</p>
          ) : (
            <div className="prose-cogniprint text-sm">
              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
                {highlightProfileReferences(message.content)}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* Checkpoint inline */}
        {!isUser && message.checkpointData && (
          <TutorCheckpoint checkpoint={message.checkpointData} messageId={message.messageId} />
        )}

        {/* Visual diagram — same brand-styled renderer as learning content */}
        {!isUser && message.visualSpec && (
          <div className="mt-3 p-3 rounded-lg bg-dark-subtle border border-dark-border">
            {message.visualSpec.caption && (
              <p className="text-xs text-content-muted font-body mb-2">{message.visualSpec.caption}</p>
            )}
            <DiagramRenderer visualSpec={message.visualSpec} />
          </div>
        )}

        {/* Video results */}
        {!isUser && message.videoResults && message.videoResults.length > 0 && (
          <TutorVideoResults videos={message.videoResults} />
        )}

        {/* Feedback + copy buttons (assistant only) */}
        {!isUser && (
          <div className="flex items-center gap-2 mt-1.5 ml-1">
            <button
              onClick={() => handleFeedback('up')}
              className={`p-1 rounded transition-colors cursor-pointer
                ${message.feedback === 'up' ? 'text-success' : 'text-content-dim hover:text-content-muted'}
              `}
              title="Helpful"
            >
              <ThumbsUp size={12} fill={message.feedback === 'up' ? 'currentColor' : 'none'} />
            </button>
            <button
              onClick={() => handleFeedback('down')}
              className={`p-1 rounded transition-colors cursor-pointer
                ${message.feedback === 'down' ? 'text-error' : 'text-content-dim hover:text-content-muted'}
              `}
              title="Not helpful"
            >
              <ThumbsDown size={12} fill={message.feedback === 'down' ? 'currentColor' : 'none'} />
            </button>
            <button
              onClick={handleCopy}
              aria-label="Copy message as markdown"
              className={`p-1 rounded transition-colors cursor-pointer
                ${copied ? 'text-success' : 'text-content-dim hover:text-content-muted'}
              `}
              title={copied ? 'Copied!' : 'Copy as markdown'}
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export function TutorConversation() {
  const messages = useTutorStore((s) => s.currentSession?.messages ?? []);
  const isLoading = useTutorStore((s) => s.isLoading);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    });
  }, [messages.length, isLoading]);

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3">
      {messages.length === 0 && !isLoading && (
        <div className="text-center py-6">
          <img src="/WavingCog.png" alt="Professor Cogni" className="w-24 h-24 mx-auto mb-3 object-contain" />
          <p className="text-sm text-content font-body font-medium mb-1">Hey! I'm Professor Cogni.</p>
          <p className="text-xs text-content-muted font-body">
            Your personal tutor. Ask me anything about what you're studying, or tap a button above.
          </p>
        </div>
      )}

      <AnimatePresence>
        {messages.map((msg) => (
          <MessageBubble key={msg.messageId} message={msg} />
        ))}
      </AnimatePresence>

      {isLoading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex justify-start items-center gap-2"
        >
          <motion.img
            src="/CogHead.png" alt="Professor Cogni" className="w-6 h-6 object-contain shrink-0"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
          <div className="bg-dark-subtle rounded-lg px-3 py-2.5 flex items-center gap-2">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-1.5 h-1.5 rounded-full bg-indigo-400"
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
              />
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}
